import { createContext, useContext, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import {
    AppId,
    cssX,
    Env,
    FCX,
    getRem,
    GtmInit,
    IDropdownItem,
    PlatformContextProvider,
    PlatformNavigation,
} from "@datassential/platform-ui-lib";
import PlatformHeader from "./PlatformHeader";
import './App.scss';
import Router from "@core/routes";
import { BrowserRouter } from "react-router-dom";
import AxiosInterceptor from "@api/AxiosInterceptors";
import { useCommonData } from "@api/useCommonData";
import { CommonDataContext, CommonDataContextType } from "@core/CommonDataContext";
import { Slide, ToastContainer } from "react-toastify";
import { ADMIN_URL } from "@core/constants";

const LIGHT_THEME_CLASS = 'theme-light';
const LIGHT_THEME_LS_NAME = 'isThemeLight';

const ThemeContext = createContext<{
    isLightThemeEnabled: boolean;
}>({
    isLightThemeEnabled: false,
});

export function useThemeContext() {
    return useContext(ThemeContext);
}

const AppContent: FCX = () => {

    return (
        <BrowserRouter>
            <PlatformNavigation/>
            <PlatformHeader/>
            <Router/>
        </BrowserRouter>
    );
}

function App() {
    const [
        isLightThemeEnabled,
        setIsLightThemeEnabled
    ] = useState(localStorage.getItem(LIGHT_THEME_LS_NAME) === 'yes');

    useEffect(() => {
        document.body.classList.toggle('theme-light', isLightThemeEnabled);
    }, [isLightThemeEnabled]);

    const { isLoading, data } = useCommonData();

    const env = useMemo(
        () => {
            switch ((process.env.REACT_APP_PLATFORM_ENV ?? '').toLowerCase()) {
                case 'qa':
                    return Env.QA;
                case 'staging':
                    return Env.Staging;
                default:
                    return Env.Live;
            }
        },
        []
    );

    const switchTheme = () => {
        const localStorageValue = localStorage.getItem(LIGHT_THEME_LS_NAME);

        if (localStorageValue === 'yes') {
            localStorage.removeItem(LIGHT_THEME_LS_NAME);
            document.body.classList.remove(LIGHT_THEME_CLASS);
            setIsLightThemeEnabled(false);
        } else {
            localStorage.setItem(LIGHT_THEME_LS_NAME, 'yes');
            document.body.classList.add(LIGHT_THEME_CLASS);
            setIsLightThemeEnabled(true);
        }
    };

    const userMenuItems = useMemo(
        (): IDropdownItem[] => {
            const result: IDropdownItem[] = [{
                id: 'theme-switch',
                content: (
                    <div
                        className={cn(cssX("Dropdown__item"), 'is-tall')}
                        onClick={switchTheme}
                    >
                        {isLightThemeEnabled ? 'Dark Theme' : 'Light Theme'}
                    </div>
                ),
            }];

            if (data.isAdmin) {
                result.push({
                    id: 'admin',
                    content: (
                        <a
                            target={'_blank'}
                            rel="noreferrer"
                            href={ADMIN_URL}
                            className={cn(cssX("Dropdown__item"), 'is-tall')}
                        >
                            Tool Admin
                        </a>
                    ),
                });
            }

            return result;
        },
        [isLightThemeEnabled, data]
    );

    const commonData = useMemo(
        (): CommonDataContextType => {
            return {
                isLoading,
                ...data,
            };
        },
        [isLoading, data]
    );

    const themeContextValue = useMemo(
        () => ({
            isLightThemeEnabled,
        }),
        [isLightThemeEnabled]
    );

    return (
        <ThemeContext.Provider value={themeContextValue}>
            <PlatformContextProvider
                appId={AppId.ConsumerPreferences}
                env={env}
                enableDevTools={env !== Env.Live}
                additionalUserMenu={userMenuItems}
            >
                <AxiosInterceptor>
                    <CommonDataContext.Provider value={commonData}>
                        {process.env.NODE_ENV !== 'development' && <GtmInit/>}
                        <AppContent/>
                        <ToastContainer
                            position="bottom-right"
                            style={{ transform: `translateY(${getRem(-40)})` }}
                            autoClose={3000}
                            hideProgressBar
                            closeOnClick
                            theme="colored"
                            transition={Slide}
                        />
                    </CommonDataContext.Provider>
                </AxiosInterceptor>
            </PlatformContextProvider>
        </ThemeContext.Provider>
    );
}

export default App;
